import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  FormatClear
} from '@mui/icons-material';

interface PolicyEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  variables?: Array<{
    key: string;
    label: string;
    description?: string;
  }>;
}

const PolicyEditor: React.FC<PolicyEditorProps> = ({ 
  value, 
  onChange, 
  placeholder = "Enter your policy here...", 
  variables = [] 
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  };

  const handleToolbarMouseDown = (e: React.MouseEvent, command: string, value?: string) => {
    e.preventDefault(); // Prevent button from stealing focus
    execCommand(command, value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
      }
    }
  };

  const handleFocus = () => {
    setIsToolbarVisible(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Delay hiding toolbar to allow toolbar button clicks to complete
    setTimeout(() => {
      if (!editorRef.current?.contains(document.activeElement)) {
        setIsToolbarVisible(false);
      }
    }, 150);
  };

  const addLink = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent button from stealing focus
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };



  return (
    <Box sx={{ position: 'relative' }}>
      {/* Toolbar */}
      {isToolbarVisible && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: 1, 
            mb: 1, 
            display: 'flex', 
            gap: 0.5,
            backgroundColor: '#f8f9fa',
            borderRadius: 1
          }}
        >
          <Tooltip title="Bold (Ctrl+B)">
            <IconButton size="small" onMouseDown={(e) => handleToolbarMouseDown(e, 'bold')}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic (Ctrl+I)">
            <IconButton size="small" onMouseDown={(e) => handleToolbarMouseDown(e, 'italic')}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline (Ctrl+U)">
            <IconButton size="small" onMouseDown={(e) => handleToolbarMouseDown(e, 'underline')}>
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bullet List">
            <IconButton size="small" onMouseDown={(e) => handleToolbarMouseDown(e, 'insertUnorderedList')}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List">
            <IconButton size="small" onMouseDown={(e) => handleToolbarMouseDown(e, 'insertOrderedList')}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Link">
            <IconButton size="small" onMouseDown={addLink}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Formatting">
            <IconButton size="small" onMouseDown={(e) => handleToolbarMouseDown(e, 'removeFormat')}>
              <FormatClear fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
              )}

      {/* Editor */}
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        sx={{
          minHeight: '120px',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          fontSize: '14px',
          lineHeight: 1.5,
          fontFamily: 'inherit',
          outline: 'none',
          backgroundColor: 'background.paper',
          '&:focus': {
            borderColor: 'primary.main',
            borderWidth: 2
          },
          '& p': {
            margin: '0 0 8px 0'
          },
          '& ul, & ol': {
            margin: '8px 0',
            paddingLeft: '24px'
          },
          '& strong': {
            fontWeight: 600
          },
          '& em': {
            fontStyle: 'italic'
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'underline'
          },
          '& .policy-variable': {
            backgroundColor: '#e3f2fd',
            padding: '2px 6px',
            borderRadius: '4px',
            color: '#1976d2',
            fontWeight: 500,
            display: 'inline-block'
          },
          '&:empty:before': {
            content: `"${placeholder}"`,
            color: 'text.secondary',
            fontStyle: 'italic'
          }
        }}
        suppressContentEditableWarning={true}
      />
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click in the editor to show formatting tools. Use Ctrl+B for bold, Ctrl+I for italic, Ctrl+U for underline.
      </Typography>
      
      {/* Available Variables */}
      {variables.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Available Variables (copy and paste into your policy):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {variables.map((variable) => (
              <Chip
                key={variable.key}
                label={`{{${variable.key}}}`}
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(`{{${variable.key}}}`);
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white'
                  }
                }}
                title={`${variable.label} - ${variable.description} (Click to copy)`}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Click any variable above to copy it, then paste into your policy text. Variables will automatically show current values to customers.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PolicyEditor; 